import _ from "lodash";
import { isBlackKey } from "../components/rolls/PianoKeybed";
import {
    AbstractHorizontalContainer,
    ContainerAtom
} from "../model/containers";
import {
    Note,
    NoteDuration,
    TimeSignature
} from "../model/core";
import { findLargestPPQ, RydProject } from "../model/project";
import { HarmonicPredicates } from "../utils/harmonyHighlight";
import { SelectionGeometry } from "../utils/NoteSelectionGroup";
import {
    CSS,
    getRenderBoxForStartAndDuration,
    gridResolutionToNormalizedWidth,
    NOTE_HEIGHT,
    NOTE_WIDTH,
    positionToTick,
    tickToPosition,
} from "./renderUtils";
import { GridResolution } from "../model/grid";
import { TrackColor } from "./trackColor";


const NOTESPACE = Array(107 + 1)
    .fill(0)
    .map((_, i) => i);

export function drawPianoRollBackground(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    noteRange: { min: number; max: number; },
    harmonicPredicates: HarmonicPredicates | undefined,
    timeSignature: TimeSignature | undefined,
    hnc: AbstractHorizontalContainer<any> | undefined,
    normalizedBarWidth: number,
    selection?: SelectionGeometry,
    zoomFactor: number = 1
) {

    const style = {
        tonalityCenterColor: CSS.VAR('--tonality-center-color'),
        tonalityNoteColor: CSS.VAR('--tonality-note-color'),
        tonalityCenterOpacity: parseFloat(CSS.VAR('--tonality-center-opacity')),
        tonalityNoteOpacity: parseFloat(CSS.VAR('--tonality-note-opacity')),
        nonTonalityNoteColor: CSS.VAR('--non-tonality-note-color'),
        nonTonalityNoteOpacity: parseFloat(CSS.VAR('--non-tonality-note-opacity')),
        kbBlack: CSS.VAR('--pr-kb-body-black'),
        kbWhite: CSS.VAR('--pr-kb-body-white'),
    };

    context.clearRect(0, 0, canvas.width, canvas.height);

    NOTESPACE
        .slice(107 - noteRange.max, 107 - noteRange.min + 1)
        .map(i => {
            return {
                i: i,
                h: (107 - i - noteRange.min) * NOTE_HEIGHT,
                n: 107 - i
            };
        })
        .forEach((y) => {
            const n = Note.fromIndex(y.n);

            if (harmonicPredicates?.hasTonality) {
                if (harmonicPredicates.isToneCenter(n)) {
                    context.fillStyle = style.tonalityCenterColor;
                    context.globalAlpha = style.tonalityCenterOpacity;
                } else if (harmonicPredicates.isInTonality(n)) {
                    context.fillStyle = style.tonalityNoteColor;
                    context.globalAlpha = style.tonalityNoteOpacity;
                } else {
                    context.fillStyle = style.nonTonalityNoteColor;
                    context.globalAlpha = style.nonTonalityNoteOpacity;
                }
            } else {
                context.fillStyle = isBlackKey(n) ? style.kbBlack : style.kbWhite;
                context.globalAlpha = 1;
            }
            context.fillRect(0, 0| (canvas.height - y.h - NOTE_HEIGHT), canvas.width, NOTE_HEIGHT);
        });

    // Draw bar overlays
    if (timeSignature && hnc) {
        const barWidthPx = normalizedBarWidth * NOTE_WIDTH;
        Array(0 | (hnc.getLength() * NOTE_WIDTH / normalizedBarWidth))
            .fill(0)
            // .filter((_, i) => i % 2 !== 0)
            .forEach((_, i) => {
                const x = i * barWidthPx;

                if (harmonicPredicates?.hasTonality) {
                    context.strokeStyle = '#040404';
                    context.globalAlpha = i % 2 ? 0.1 : 0;
                } else {
                    context.strokeStyle = CSS.VAR('--vi-p-11');
                    context.globalAlpha = i % 2 ? 0.4 : 0;
                }

                context.fillRect(0|x + 0.5, 0, 0|barWidthPx - 0.5, canvas.height);
            });
    }

    if (selection) {
        context.fillStyle = '#181c6a';
        context.globalAlpha = parseFloat(CSS.VAR('--selection-opacity'));
        context.fillRect(
            selection.left * NOTE_WIDTH,
            canvas.height - (selection.height * NOTE_HEIGHT),
            selection.width * NOTE_WIDTH,
            selection.height
        );
    }
}

type CollapsedNoteCanvasCoordinates = {
    x: number,
    note: number,
    width: number,
    height: number,
}

export function drawPianoRollCollapsed(
    canvas: HTMLCanvasElement,
    resolution: NoteDuration,
    grid: ContainerAtom<Note>[][],
    noteColor: TrackColor.Color,
    canvasWidth: number,
    canvasHeight: number, // Fixed height for the collapsed view
    zoomFactor: number,
    ppq: number,
    offset: number = 0

) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight; // Fixed height for the collapsed view
    // make treansparent background
    // FIXME: turn back to 0
    ctx.globalAlpha = 0;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.globalAlpha = 1;
    ctx.lineWidth = 1;

    ctx.fillStyle = noteColor; // Background color
    ctx.strokeStyle = noteColor; // Grid line color

    let [min, max] = [200, -1];

    grid.map((row, i) => {
        if (!row) return;
        // if (i < offset) return;
        return row.map((atom, j) => {

            const note = atom.payload.index;
            const start = i;
            const duration = atom.duration.value;
            const box = getRenderBoxForStartAndDuration(start, atom.duration, ppq);

            if (note < min) {
                min = note;
            }
            if (note > max) {
                max = note;
            }

            return {
                x: start * NOTE_WIDTH / zoomFactor,
                note: note,
                width: box.width / zoomFactor * 4,
                height: 1
            } as CollapsedNoteCanvasCoordinates;
        });
    }).flat().forEach((c: CollapsedNoteCanvasCoordinates, i) => {
        const range = max - min + 2;
        const vStretch = 106 / (range >= 10 ? range : 10);

        const y = canvas.height * ((max - c.note + 1) / 107)
        ctx.fillRect(
            c.x - offset,
            0 | (y * vStretch),
            c.width,
            0 | (c.height * (vStretch > 3 ? vStretch / 2 : vStretch * 2))
        );
    });
}

export function drawPianoRollGridlines(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    ppq: number,
    zoomFactor: number,
    gridResolution: GridResolution,
    timeSignature?: TimeSignature,
) {
    const interval = gridResolutionToNormalizedWidth(gridResolution, ppq, timeSignature) * NOTE_WIDTH / zoomFactor;

    // FIXME: make this prerendered and only apply over the other drawings
    context.globalAlpha = 1;
    context.strokeStyle = '#ffffff';
    context.lineWidth = 0.1;
    let pos = interval;
    do {
        context.moveTo(pos - 1, 0);
        context.lineTo(pos - 1, canvas.height);
        context.stroke();
        pos += interval;
    } while (pos <= canvas.width);
}

function drawBar(
    ctx: CanvasRenderingContext2D,
    barPos: number,
    barCount: number,
    canvasHeight: number
) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#e0e0e0'; //CSS.VAR('--timeline-header-foreground');
    ctx.lineWidth = 0.5;
    ctx.moveTo(barPos - 1, 0);
    ctx.lineTo(barPos - 1, canvasHeight);
    ctx.stroke();

    // Draw bar number
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText((barCount + 1).toString(), barPos + 2, 2);
}

function drawBeat(
    ctx: CanvasRenderingContext2D,
    pos: number,
    measureCount: number,
    barCount: number,
    canvasHeight: number
) {
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#808080'; //CSS.VAR('--timeline-header-foreground');
    ctx.lineWidth = 0.5;
    ctx.moveTo(pos - 1, 0 | canvasHeight / 2);
    ctx.lineTo(pos - 1, canvasHeight);
    ctx.stroke();

    // Draw measure number
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(`${barCount + 1}.${measureCount + 1}`, pos + 2, 10);

}

export function drawTimelineHeader(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    project: RydProject,
    canvasWidth: number, // must be provided with 2x the actual width for high-dpi screens
    zoomFactor: number,
    offset: number
) {

    context.clearRect(0, 0, canvas.width, canvas.height);
    const backgrouncColor = '#181A1F';

    const ppq = findLargestPPQ(project);
    const timeSignature = project.contextFrame.computed.timeSignature;

    canvas.width = canvasWidth;
    canvas.height = 20;
    context.clearRect(0, 0, canvas.width, canvas.height);

    context.fillStyle = backgrouncColor ; //CSS.VAR('--timeline-header-foreground');
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.globalAlpha = 1;
    context.strokeStyle = '#e0e0e0' ; //CSS.VAR('--timeline-header-foreground');
    context.fillStyle = '#e0e0e0' ; //CSS.VAR('--timeline-header-foreground');
    context.lineWidth = 1;

    // console.time('drawing-timeline');

    const minTick = Math.min(
        positionToTick(NOTE_WIDTH, zoomFactor, offset),
        project.getLength()
    );
    const maxTick = Math.min(
        positionToTick(NOTE_WIDTH, zoomFactor, offset + canvas.width),
        project.getLength()
    );

    const minBeat = 0 | (minTick / ppq);
    const maxBeat = 0 | (maxTick / ppq);

    const stepFactor = (0 | zoomFactor / ppq) + 1;
    // console.log('stepFactor', stepFactor,zoomFactor, ppq);

    _.range(minBeat, maxBeat + 1)
        .map(beat => {
            return {
                pos: tickToPosition(NOTE_WIDTH, zoomFactor, beat * ppq) - offset,
                beat: beat
            };
        })
        .filter(p => p.pos >= 0 && p.pos <= canvas.width)
        .forEach((pos, i) => {
            const showBar = pos.beat % timeSignature.numerator === 0 && (stepFactor == 1 || pos.beat % (2 ** stepFactor) == 0);
            const barCount = 0 | (pos.beat / timeSignature.numerator);
            if (showBar) {
                drawBar(context, pos.pos, barCount, canvas.height);
            }
            if (stepFactor == 1 || pos.beat % (2 ** stepFactor) == 0)  {
                drawBeat(context, pos.pos, pos.beat % timeSignature.numerator, barCount, canvas.height);
            }
        });

    // console.timeEnd('drawing-timeline');
}

export function drawMixerAudioMeterEmpty(
    context: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number
) {
    context.clearRect(0, 0, canvasWidth, canvasHeight);
    context.fillStyle = 'black';
    context.fillRect(0, 0, canvasWidth, canvasHeight);
}
